import type { PromiseCompletion } from "../classes/PromiseCompletion";
import { notificationService } from "./NotificationService";
import { onlineService } from "./OnlineService";

export interface AjaxOptions extends RequestInit {
  loader?: PromiseCompletion;
  cancellationToken?: AbortSignal;
  defaultErrorHandling?: boolean; // Whether to show error notifications by default (can be overridden by individual calls)
  onRateLimit?: () => void; // Optional callback for rate limit handling
  actualize?: boolean;
}

export class OfflineError extends Error {
  constructor() {
    super('Cannot save while offline. Changes will sync when you are back online.');
    this.name = 'OfflineError';
  }
}

let refreshTokenPromise: Promise<void> | null = null;

export class HttpError extends Error {
  code: number;
  url: string;
  method: string;
  constructor(code: number, message: string, url: string, method: string){
    super(message);
    this.name = 'HttpError';
    this.code = code;
    this.url = url;
    this.method = method;
  }
}

class ApiService {

  // The app is read-only while offline.
  private _ensureOnline(): void {
    if (!onlineService.isOnline) {
      throw new OfflineError();
    }
  }

  private async _execFetch(url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', options?: AjaxOptions): Promise<Response> {
    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string>),
    };

    if (options?.actualize) {
      headers['X-Actualize'] = '1';
    }

    const request = fetch(url, {
      method,
      headers,
      body: options?.body,
      signal: options?.cancellationToken ? options.cancellationToken : undefined
    });

    options?.loader?.subscribe(request);

    const response = await request;

    if (response.status === 401) {

      if (refreshTokenPromise){
        await refreshTokenPromise;
      } else {
        refreshTokenPromise = new Promise(async (resolve, reject) => {
          try {
            const usernameCookie = document.cookie.split('; ').find(row => row.startsWith('username='));
            if (usernameCookie) {
              //try to refresh the token
              const refreshRequest = fetch('/auth/refresh', {
                method: 'POST'
              });
              if (options?.loader) {
                options?.loader.subscribe(refreshRequest);
              }

              const refreshResponse = await refreshRequest;
              if (refreshResponse.ok) {
                // If the refresh was successful, resolve the promise
                resolve();
              } else {
                // If the refresh failed, redirect to login
                if (refreshResponse.status === 401) {
                  reject(new Error('Failed to refresh token. Redirecting to login.'));
                  window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
                } else {
                  reject(new Error(`Failed to execute request due to token expiration and failed to refresh token. Status: ${refreshResponse.status}, online: ${navigator.onLine}`));
                }
              }
            } else {
              reject(new Error('No username cookie found.'));
            }
          } catch (error) {
            reject(error);
          } finally {
            refreshTokenPromise = null; // Reset the promise after completion
          }
        }
      );
      }
      await refreshTokenPromise;
      return this._execFetch(url, method, options); // Retry the request after refreshing the token
    }

    if (!response.ok && options?.defaultErrorHandling !== false) {
      notificationService.error(`Failed to execute request. Url: ${url} Code: ${response.status}, statusText: ${response.statusText}`);
      throw new HttpError(response.status, response.statusText, url, method);
    }

    if (response.status === 429 && options?.onRateLimit) {
      options.onRateLimit();
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    if (!response.ok) {
      throw new HttpError(response.status, response.statusText, url, method);
    }

    return response;
  }

  public async get<T>(url: string, query?: Record<string, string | null> | null, options?: AjaxOptions): Promise<T> {
    const queryFiltered: Record<string, string> | null = query ? Object.fromEntries(Object.entries(query).filter(([_, value]) => value !== null)) as Record<string, string> : null;
    const queryString = queryFiltered ? '?' + new URLSearchParams(queryFiltered).toString() : '';
    const response = await this._execFetch(url + queryString, 'GET', options);

    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }

  public async post<T = unknown>(url: string, data?: unknown, options?: AjaxOptions): Promise<T> {
    this._ensureOnline();
    const response = await this._execFetch(url, 'POST', {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      loader: options?.loader
    });

    const contentType = response.headers.get('Content-Type') || '';
    const isJson = contentType.includes('application/json');
    const jsonParseTask = isJson ? response.json() : Promise.resolve(null);
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    if (!response.ok) {
      throw new HttpError(response.status, response.statusText, url, 'POST');
    }

    const result = await jsonParseTask;
    return result as T;
  }
  
  public async put<T = unknown>(url: string, data?: unknown, options?: AjaxOptions): Promise<T> {
    this._ensureOnline();
    const response = await this._execFetch(url, 'PUT', {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      loader: options?.loader
    });

    const contentType = response.headers.get('Content-Type') || '';
    const isJson = contentType.includes('application/json');
    const jsonParseTask = isJson ? response.json() : Promise.resolve(null);
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    if (!response.ok) {
      throw new HttpError(response.status, response.statusText, url, 'PUT');
    }

    const result = await jsonParseTask;
    return result as T;
  }

  public async delete(url: string, options?: AjaxOptions): Promise<void> {
    this._ensureOnline();
    const response = await this._execFetch(url, 'DELETE', options);

    if (!response.ok) {
      throw new HttpError(response.status, response.statusText, url, 'DELETE');
    }
  }

  /** Clears auth cookies server-side, then redirects to login. */
  public async logout(): Promise<void> {
    await fetch('/auth/logout', { method: 'POST' });
    window.location.href = '/auth/login';
  }

  defaultErrorHandler(error: HttpError) {
    console.error('API error:', error);
    notificationService.error(`Error execution request. Url: ${error.method} ${error.url} Code: ${error.code}`);
  }
}

export const apiService = new ApiService();
export default apiService;